'use client';

import { Link } from '@hc-frontend/core-ui';
import { usePathname } from 'next/navigation';
import type React from 'react';
import { useEffect, useState } from 'react';
function NotFoundBox({
  title,
  content,
  cta: { children: ctaChildren, ...ctaProps },
}: {
  title: React.ReactNode;
  content: React.ReactNode;
  cta: React.ComponentProps<typeof Link>;
}) {
  return (
    <div className="hc-box-layout flex flex-col items-center justify-between gap-16-24 rounded bg-primary-background-50 p-16-24">
      <p className="hc-typography--paragraph-lg font-bold">{title}</p>
      <p className="hc-typography--paragraph-sm">{content}</p>
      <Link
        className="hc-btn--secondary-card inline-block w-fit !p-12-12 text-secondary-900"
        {...ctaProps}
      >
        {ctaChildren}
      </Link>
    </div>
  );
}

/**
 * This is needed to force a hard load of the external routes that do
 * not exist on this application.
 */
const SUPPORTED_EXTERNAL_ROUTES = [
  '/medicare-insurance/typ-tranzact-out-hours/',
  '/medicare-insurance/typ-tranzact-weekend/',
];

export function NotFoundContent() {
  const pathname = usePathname();
  const [isNotFound, setIsNotFound] = useState(false);

  useEffect(() => {
    if (
      SUPPORTED_EXTERNAL_ROUTES.includes(pathname) &&
      window.location.hostname !== 'localhost' // Avoid infinite loop in local development
    ) {
      window.location.href = pathname;
      setIsNotFound(false);
    } else {
      setIsNotFound(true);
    }
  }, [pathname]);

  if (isNotFound)
    return (
      <div className="hc-container-layout hc-stack-layout justify-center py-32-60 text-center">
        <h1 className="hc-typography--heading-two">
          Oh no! We couldn't find the page you were searching for.
        </h1>
        <h5 className="hc-typography--paragraph-lg">
          Let's get you back on track.
        </h5>
        <div
          className="hc-switcher-layout"
          style={{
            '--hc-switcher-layout-break': '600px',
          }}
        >
          <NotFoundBox
            title="Healthcare.com home page"
            content="Run an insurance quote, get tools and advice or find services."
            cta={{
              children: 'Home Page',
              href: '/',
              id: 'not-found-page-home-link',
            }}
          />
          <NotFoundBox
            title="Insurance Quotes"
            content="Compare and enroll online or talk to a licensed insurance agent."
            cta={{
              children: 'Insurance Quotes',
              href: 'https://quotes.healthcare.com',
              id: 'not-found-page-quotes-link',
            }}
          />
        </div>
      </div>
    );

  return <div data-testid="empty" />;
}
